import Moment from 'moment'
import * as Redux from 'redux'
import * as IsActive from '../../components/is_active'
import * as Modal from '../../components/modal'
import * as Sortable from '../../components/sortable'
import * as Request from '../../request'
import * as Actions from './actions'
import * as Constants from './constants'
import * as Types from './types'

export const emptyAddEventModalState: Types.ModalData = {
  community_identifier: '',
  days: 'M',
  frequency: 'weekly',
  name: '',
  preferences: [],
  schedule_type: '',
  time: '05:00',
  to_email: '',
}

const modalReducerEvent: Modal.ReducerEvent<Types.ModalData> = {
  initializeState: () => emptyAddEventModalState,
  requestEvent: Constants.ADD_EVENT,
}

const normalizeTypes: Sortable.TypeNormalizer<Types.Event> =
  (payload): Sortable.SortableData<Types.Event> => {
  return payload.map((e: Types.Event) => ({
    ...e,
    last_run_datetime: Moment(e.last_run),
  }))
}

const reducerEvent: Sortable.ReducerEvent<Types.Event> = {
  defaultSort: { field: 'community_identifier', dir: 'asc' },
  normalizer: normalizeTypes,
  requestEvent: Constants.FETCH_EVENTS,
}

type ModalActions = Redux.Action & IsActive.StateChangeActionObject & Actions.EditEventModalAction & any

// General purpose signature of modal reducers.
type EventModalReducer = Redux.Reducer<Modal.State<Types.ModalData>>

// Listens for the Edit action and populates the modal data with the currently selected data
export const editModalReducer: EventModalReducer =
  (state: Modal.State<Types.ModalData>, action: ModalActions): Modal.State<Types.ModalData> => {
  switch (action.type) {
    case Constants.EDIT_EVENT:
      return {
        ...state,
        data: {
          ...state.data,
          community_identifier: action.event.community_identifier,
          days: action.event.days,
          frequency: action.event.frequency,
          id: action.event.id,
          name: action.event.name,
          preferences: action.event.preferences,
          schedule_type: action.event.schedule_type,
          time: action.event.time,
          to_email: action.event.email_list,
        },
        isOpen: true,
        modalTitle: 'Edit Report',
      }
    case Request.requestConstant(Constants.UPDATE_EVENT):
      return {
        ...state,
        isSubmitting: true,
      }
    case Request.errorConstant(Constants.UPDATE_EVENT):
      return {
        ...state,
        isSubmitting: false,
      }
    case Request.successConstant(Constants.UPDATE_EVENT):
      return {
        ...state,
        isOpen: false,
        isSubmitting: false,
      }
    case Request.successConstant(Constants.UPDATE_USER_COMMUNITY_PREFERENCE):
      const reqUrlParts = (((action.payload || {}).request || {}).url || '').split('/')
      const ucpid = parseInt(reqUrlParts[reqUrlParts.length - 1], 10)
      const foundIndex = state.data.preferences.findIndex((p) => p.id === ucpid)
      const changedEmail = state.data.preferences[foundIndex].email_address
      const oldToEmail = state.data.to_email ? state.data.to_email.split(',').map((e) => e.trim()) : []
      const emailFoundIndex = oldToEmail.indexOf(changedEmail) // find the index of changed email
      if (emailFoundIndex >= 0) {
        oldToEmail.splice(emailFoundIndex, 1) // remove the email
      } else {
        oldToEmail.push(changedEmail)
      }
      const newToEmail = oldToEmail.join(',') // construct the new email

      if (foundIndex >= 0) {
        return {
          ...state,
          data: {
            ...state.data,
            email_list: newToEmail,
            preferences: state.data.preferences.map((preference) => {
              return {
                ...preference,
                is_active: preference.id === ucpid ? !preference.is_active : preference.is_active,
              }
            }),
            to_email: newToEmail,
          },
        }
      } else {
        return state
      }
    case Modal.toggleModalConstant(modalReducerEvent.requestEvent):
      return {
        ...state,
        data: emptyAddEventModalState,
        modalTitle: 'Add New Report',
      }

    case Constants.UPDATE_SCHEDULE_TYPE:
      return {
        ...state,
        data: {
          ...state.data,
        schedule_type: action.event.schedule_type,
        },
      }
    default:
      return state
  }
}

export const scheduleTypeReducer: any =
  (state: string[] = [], action: Request.SuccessAction) => {
  switch (action.type) {
    case Request.successConstant(Constants.FETCH_SCHEDULE_TYPES):
      return [
        ...action.payload.response,
      ]
    default:
      return state
  }
}

type AllActions = Redux.Action &
                  Request.SuccessAction &
                  Sortable.SortAction &
                  IsActive.FilterActionObject

export const eventsViewReducer = (state: Types.ViewState, action: AllActions) => {
  switch (action.type) {
    case Request.requestConstant(Constants.FETCH_EVENTS):
      return {
        ...state,
        isLoading: true,
      }
    case Request.successConstant(Constants.FETCH_EVENTS):
      return {
        ...state,
      }
    case Constants.SELECT_SORT_AND_FILTER:
      return {
        ...state,
        filter: action.filter,
        sort: state.sort,
      }
    default:
      return state
  }
}
export const dataReducer =
  (state: Types.Event[], action: AllActions) => {
  switch (action.type) {
    case Request.successConstant(Constants.UPDATE_USER_COMMUNITY_PREFERENCE):
      const reqUrlParts = action.requestAction.options.url.split('/')
      const ucpid = parseInt(reqUrlParts[reqUrlParts.length - 1], 10)
      const eventItem = state.filter((event) => event.preferences.filter((x) => x.id === ucpid).length > 0)[0]
      const foundIndex = eventItem.preferences.findIndex((p) => p.id === ucpid)
      const changedEmail = eventItem.preferences[foundIndex].email_address
      const oldToEmail = eventItem.email_list ? eventItem.email_list.split(',').map((e) => e.trim()) : []
      const emailFoundIndex = oldToEmail.indexOf(changedEmail) // find the index of changed email
      if (emailFoundIndex >= 0) {
        oldToEmail.splice(emailFoundIndex, 1) // remove the email
      } else {
        oldToEmail.push(changedEmail)
      }
      const newToEmail = oldToEmail.join(',') // construct the new email
      return state.map((x) => {
        return {
          ...x,
          email_list: x.id === eventItem.id ? newToEmail : x.email_list,
          preferences: x.preferences.map((preference) => {
            return {
              ...preference,
              is_active: preference.id === ucpid ? !preference.is_active : preference.is_active,
            }
          }),
        }
      })
    default:
      return state
  }
}

export const reducer = Redux.combineReducers({
  data: Request.reducerChain([
    Sortable.stateReducer(reducerEvent), IsActive.dataReducer(Constants.UPDATE_EVENT), dataReducer,
  ]),
  modal: Request.reducerChain([Modal.reducer(modalReducerEvent), editModalReducer]),
  scheduleTypes: scheduleTypeReducer,
  view: Request.reducerChain([
    eventsViewReducer,
    Sortable.viewReducer(reducerEvent),
  ]),
})



// WEBPACK FOOTER //
// ./src/pages/admin-reports/reducer.ts